import { isAirportBusTransportType } from "../../PublicTransport/AirportBus/providers";
import { DEUTSCHE_BAHN_SLUG } from "../../PublicTransport/DeutscheBahn/constants";

export const MILES_AND_MORE_NUMBER_LENGTH = 15;

export const MILES_AND_MORE_PREFIXES = ["3330", "2220", "9922", "9920", "9999"] as const;

const MILES_AND_MORE_NUMBER_PATTERN = /^(3330|2220|9922|9920|9999)\d{11}$/;

/**
 * Miles & More is optional: an empty value is valid.
 * When provided, the number must be 15 digits and start with a supported prefix.
 */
export const isValidMilesAndMoreNumber = (value: string): boolean => {
  if (!value) {
    return true;
  }

  return MILES_AND_MORE_NUMBER_PATTERN.test(value);
};

export const startOfDay = (date: Date) => {
  const normalizedDate = new Date(date);
  normalizedDate.setHours(0, 0, 0, 0);
  return normalizedDate;
};

export const isTodayOrFutureDate = (date: Date | null) => {
  if (!(date instanceof Date) || isNaN(date.getTime())) {
    return false;
  }

  return startOfDay(date) >= startOfDay(new Date());
};

/** POST payload row must use the same `type` string as the GET transport-offers item (strict `===`). */
/**
 * Transport types whose airline search row `details` (the arrival station / stop picked server-side)
 * are passed on to the provider page via `typeSpecific.details`.
 */
export const shouldPassTransportDetailsToProvider = (transportType: string | null | undefined): boolean => {
  const type = transportType?.toLowerCase();
  if (!type) {
    return false;
  }
  return ["sbb", "obb", DEUTSCHE_BAHN_SLUG].includes(type) || isAirportBusTransportType(type);
};

export const findMatchingTransportRow = (data: any[] | null | undefined, offerType: string) => {
  if (!Array.isArray(data) || offerType == null || offerType === "") {
    return null;
  }
  return data.find((row: any) => row?.type === offerType) ?? null;
};

export const enrichAirportOffers = (
  airportOffers: any[] | null | undefined,
  transportSearchByType: Record<string, any> | null | undefined
) => {
  const searchByType = transportSearchByType || {};
  const transportSearchActive = Object.keys(searchByType).length > 0;

  return (airportOffers || []).flatMap((offer: any) => {
    const offerType = offer?.type;
    if (!offerType) {
      return [];
    }

    if (!transportSearchActive) {
      return [
        {
          ...offer,
          offer_code: undefined,
          formatted_price: undefined,
          miles_and_more: undefined
        }
      ];
    }

    const typeState = searchByType[offerType];
    if (!typeState) {
      return [];
    }

    if (typeState.isLoading) {
      return [
        {
          ...offer,
          offer_code: undefined,
          formatted_price: undefined,
          miles_and_more: undefined
        }
      ];
    }

    if (typeState.error || !typeState.isSuccess) {
      return [];
    }

    const transportOffer = findMatchingTransportRow(typeState.data, offerType);
    if (!transportOffer) {
      return [];
    }

    return [
      {
        ...offer,
        offer_code: transportOffer.offer_code,
        formatted_price: transportOffer.formatted_price,
        miles_and_more: transportOffer.miles_and_more
      }
    ];
  });
};
